// /src/components/CalendlyButton.tsx
import React, { useState, useEffect } from "react";
import {
  getTecnicoCalendlyByEmail,
  TecnicoCalendly,
} from "../constants/tecnicosCalendly";

interface CalendlyButtonProps {
  dueno_email?: string | null;
  dueno_nombre?: string | null;
  soporteData: {
    id_concurso: string;
    id_usuario: string;
    titulo: string;
    codigo_expediente: string;
    cliente_nombre: string;
    cliente_email: string;
  };
  buttonText?: string;
  variant?: "primary" | "secondary" | "compact";
  style?: React.CSSProperties;
}

const CalendlyButton: React.FC<CalendlyButtonProps> = ({
  dueno_email,
  dueno_nombre,
  soporteData,
  buttonText = "📅 Agendar reunión",
  variant = "primary",
  style = {},
}) => {
  const [tecnicoCalendly, setTecnicoCalendly] =
    useState<TecnicoCalendly | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (dueno_email) {
      const calendly = getTecnicoCalendlyByEmail(dueno_email);
      setTecnicoCalendly(calendly);
    } else {
      setTecnicoCalendly(null);
    }
  }, [dueno_email]);

  const handleScheduleClick = () => {
    if (!tecnicoCalendly) return;

    setIsLoading(true);

    // Crear información detallada para Calendly
    const informacionDetallada = `
- Cod expediente: ${soporteData.codigo_expediente}
- Titulo concurso: ${soporteData.titulo}
- Nombre cliente: ${soporteData.cliente_nombre}
- Enlace concurso: ${window.location.origin}/concurso/${soporteData.id_concurso}
- Enlace soporte: ${window.location.href}
`.trim();

    // Crear parámetros para pre-llenar el formulario de Calendly
    const params = new URLSearchParams({
      name: soporteData.cliente_nombre,
      email: soporteData.cliente_email,
      a1: informacionDetallada, // Toda la información en el primer campo personalizado
    });

    // URL completa de Calendly con parámetros
    const calendlyUrl = `${tecnicoCalendly.calendly_url}?${params.toString()}`;

    // Abrir Calendly en popup
    const popup = window.open(
      calendlyUrl,
      "calendly-popup",
      "width=900,height=700,scrollbars=yes,resizable=yes,centerscreen=yes"
    );

    // Detectar cuando se cierre el popup
    const checkClosed = setInterval(() => {
      if (popup?.closed) {
        clearInterval(checkClosed);
        setIsLoading(false);
        console.log("Calendly popup closed");
        // Aquí podrías refrescar datos o mostrar una confirmación
      }
    }, 1000);

    // Timeout de seguridad
    setTimeout(() => {
      if (!popup?.closed) {
        clearInterval(checkClosed);
        setIsLoading(false);
      }
    }, 300000); // 5 minutos
  };

  // Si no hay técnico asignado o no tiene Calendly configurado, no mostrar
  if (!dueno_email || !tecnicoCalendly) {
    return null;
  }

  const getButtonClasses = () => {
    const baseClasses = "btn";
    switch (variant) {
      case "secondary":
        return `${baseClasses} btn-secondary`;
      case "compact":
        return `${baseClasses} btn-sm btn-outline-primary`;
      default:
        return `${baseClasses} btn-primary`;
    }
  };

  return (
    <div>
      <button
        className={getButtonClasses()}
        onClick={handleScheduleClick}
        disabled={isLoading}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          ...style,
        }}
      >
        {isLoading ? (
          <>
            <span className="spinner-border spinner-border-sm" />
            Abriendo calendario...
          </>
        ) : (
          <>
            {buttonText}
          </>
        )}
      </button>

      {/* Información adicional */}
      <small className="text-muted d-block mt-1">
        Se abrirá en una nueva ventana con tus datos pre-llenados
      </small>
    </div>
  );
};

export default CalendlyButton;
